import React, { useState } from 'react';
import { Phone, Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Link, useNavigate } from 'react-router-dom';
import { useSiteSettings } from '../context/SiteSettingsContext';

const FacebookIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
);
const InstagramIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
);

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { settings } = useSiteSettings();

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'Inventory', href: '/inventory' },
    { name: 'Segway', href: '/segway' },
    { name: 'About Us', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  const inventoryMenu = [
    { name: 'All Inventory', href: '/inventory' },
    { name: 'Vehicles', href: '/vehicles' },
    { name: 'Power Sports', href: '/powersports' },
    { name: 'Marine', href: '/marine' },
  ];

  return (
    <header className="bg-white sticky top-0 z-50 shadow-sm">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center" data-testid="header-logo-link">
            <img src={settings.logoUrl || '/logo-trimmed.png'} alt={settings.businessName} className="h-16 md:h-[72px] w-auto object-contain" data-testid="header-logo" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              link.name === 'Inventory' ? (
                <div key="Inventory" className="relative group">
                  <Link to={link.href} className="text-gray-700 hover:text-red-600 font-medium transition-colors inline-flex items-center gap-1" data-testid="header-nav-inventory">
                    Inventory<ChevronDown size={15} className="transition-transform group-hover:rotate-180" />
                  </Link>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="bg-white rounded-xl shadow-xl ring-1 ring-gray-100 py-2 min-w-[200px]">
                      {inventoryMenu.map((item) => (
                        <Link key={item.href} to={item.href} className="block px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors" data-testid={`header-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}>{item.name}</Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link key={link.name} to={link.href} className="text-gray-700 hover:text-red-600 font-medium transition-colors relative group" data-testid={`header-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  {link.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 group-hover:w-full"></span>
                </Link>
              )
            ))}
          </div>

          {/* Phone + single CTA */}
          <div className="hidden md:flex items-center gap-5">
            <a href={`tel:${settings.phone}`} className="inline-flex items-center gap-2 text-gray-800 hover:text-red-600 font-semibold transition-colors" data-testid="header-phone">
              <Phone size={18} className="text-red-600" />
              {settings.phone}
            </a>
            <Button onClick={() => navigate('/apply-now')} className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 rounded-lg font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-red-600/30" data-testid="get-pre-approved-btn">
              Apply Now
            </Button>
          </div>

          {/* Mobile menu button */}
          <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn" aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileMenuOpen} aria-controls="mobile-nav">
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div id="mobile-nav" className="md:hidden py-4 border-t">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                link.name === 'Inventory' ? (
                  <div key="Inventory" className="flex flex-col">
                    <Link to="/inventory" className="text-gray-700 hover:text-red-600 font-medium transition-colors px-2 py-2" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-inventory">Inventory</Link>
                    <div className="flex flex-col border-l-2 border-gray-100 ml-3">
                      {inventoryMenu.slice(1).map((item) => (
                        <Link key={item.href} to={item.href} className="text-gray-600 hover:text-red-600 text-sm px-4 py-1.5 transition-colors" onClick={() => setMobileMenuOpen(false)} data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}>{item.name}</Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link key={link.name} to={link.href} className="text-gray-700 hover:text-red-600 font-medium transition-colors px-2 py-2" onClick={() => setMobileMenuOpen(false)} data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}>{link.name}</Link>
                )
              ))}
              <Link to="/book-test-drive" className="text-gray-700 hover:text-red-600 font-medium transition-colors px-2 py-2" onClick={() => setMobileMenuOpen(false)}>Book a Test Drive</Link>
              <a href={`tel:${settings.phone}`} className="flex items-center gap-2 text-gray-700 hover:text-red-600 font-medium px-2 py-2">
                <Phone size={18} className="text-red-600" />{settings.phone}
              </a>
              <Button onClick={() => { navigate('/apply-now'); setMobileMenuOpen(false); }} className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium w-full mt-2" data-testid="mobile-apply-now-btn">Apply Now</Button>
              {(settings.facebookUrl || settings.instagramUrl) && (
                <div className="flex items-center justify-center gap-6 pt-4 mt-2 border-t">
                  {settings.facebookUrl && <a href={settings.facebookUrl} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-gray-600 hover:text-red-600"><FacebookIcon /></a>}
                  {settings.instagramUrl && <a href={settings.instagramUrl} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-gray-600 hover:text-red-600"><InstagramIcon /></a>}
                </div>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
